import { Component, Host, h, Element, Prop, Method, Watch, Event, EventEmitter, State, Listen } from "@stencil/core";

import {
  StzhTimelineItemOpenEvent,
  StzhTimelineItemOpenedEvent,
  StzhTimelineItemCloseEvent,
  StzhTimelineItemClosedEvent,
  StzhTimelineItemFocusEvent,
  StzhTimelineItemBlurEvent,
  StzhTimelineFoldableChangeEvent,
  StzhBadgeType,
} from "../../index";

import { waitForEvent } from "../../utils/event-utils";
import { stopAnimations, animateTo, shimKeyframesAutoValues } from "../../utils/animation-utils";
import { hasSlot } from "../../utils/utils";

const ANIMATION_SHOW = {
  keyframes: [{ height: "0" }, { height: "auto" }],
  options: { duration: 300, easing: "linear" },
};

const ANIMATION_HIDE = {
  keyframes: [{ height: "auto" }, { height: "0" }],
  options: { duration: 300, easing: "linear" },
};

let timelineItemCounter = 0;

/**
 * @slot - Slot for any content
 * @slot icon - Slot for icon element
 */
@Component({
  tag: "stzh-timeline-item",
  styleUrl: "stzh-timeline-item.scss",
  shadow: true,
})
export class StzhTimelineItem {
  /** Whether folds are foldable (overwrites `foldable` property from `timeline` parent) */
  @Prop() foldable: boolean;

  /** Whether fold is collapsible (overwrites `collapsible` property from `timeline` parent) */
  @Prop() collapsible: boolean;

  /** Whether fold is open */
  @Prop({ mutable: true, reflect: true }) open: boolean = false;

  /** Whether fold disabled */
  @Prop({ reflect: true }) disabled: boolean = false;

  /** Whether step should be marked as completed */
  @Prop({ reflect: true }) completed: boolean = false;

  /** Heading title */
  @Prop() heading: string = "";

  /** Description */
  @Prop() description: string = "";

  /** Step number shown inside bullet by default */
  @Prop({ reflect: true }) step: number = 1;

  /** Icon (use instead of icon slot) */
  @Prop() icon: string = "";

  /** Badge */
  @Prop() badge: string = "";

  /** Whether badge should be displayed empty */
  @Prop({ reflect: true }) badgeEmpty: boolean = false;

  /** Badge type */
  @Prop({ reflect: true }) badgeType: StzhBadgeType = "default";

  /** Heading level */
  @Prop({ reflect: true }) headingLevel: "1" | "2" | "3" | "4" = "3";

  /**
   * For Adobe Analytics.
   * Will be rendered as value of `s-object-id` attribute to the button element.
   * Default value will be taken from `heading` prop.
   */
  @Prop() analyticsId: string;

  @Element() element: HTMLStzhTimelineItemElement;

  /** Open event */
  @Event() stzhOpen: EventEmitter<StzhTimelineItemOpenEvent>;

  /** Opened event (after animation) */
  @Event() stzhOpened: EventEmitter<StzhTimelineItemOpenedEvent>;

  /** Close event */
  @Event() stzhClose: EventEmitter<StzhTimelineItemCloseEvent>;

  /** Closed event (after animation) */
  @Event() stzhClosed: EventEmitter<StzhTimelineItemClosedEvent>;

  /** Show timeline item content */
  @Method()
  async show() {
    if (this.open || this.disabled || !this._foldable) {
      return;
    }

    this.toggledByMethod = true;
    this.open = true;
    return waitForEvent(this.element, "stzhOpened");
  }

  /** Hide timeline item content */
  @Method()
  async hide() {
    if (!this.open || this.disabled || !this._foldable) {
      return;
    }

    this.toggledByMethod = true;
    this.open = false;
    return waitForEvent(this.element, "stzhClosed");
  }

  /** Toggle timeline item content */
  @Method()
  async toggle() {
    if (this.open) {
      return await this.hide();
    } else {
      return await this.show();
    }
  }

  /** Focus event */
  @Event() stzhFocus: EventEmitter<StzhTimelineItemFocusEvent>;

  /** Input blur event */
  @Event() stzhBlur: EventEmitter<StzhTimelineItemBlurEvent>;

  @Watch("open")
  async openWatcher(newValue: boolean) {
    if (!this.bodyElement) {
      return;
    }

    if (newValue) {
      if (this.toggledByMethod) {
        this.stzhOpen.emit({
          component: "stzh-timeline-item",
        });
      }

      await stopAnimations(this.bodyElement);
      this.bodyElement.hidden = false;

      await animateTo(
        this.bodyElement,
        shimKeyframesAutoValues(ANIMATION_SHOW.keyframes, {
          height: this.bodyElement.scrollHeight,
        }),
        ANIMATION_SHOW.options,
      );

      this.bodyElement.style.height = "auto";
      this.bodyElement.style.overflow = "visible";

      if (this.toggledByMethod) {
        this.stzhOpened.emit({
          component: "stzh-timeline-item",
        });
      }
    } else {
      if (this.toggledByMethod) {
        this.stzhClose.emit({
          component: "stzh-timeline-item",
        });
      }

      await stopAnimations(this.bodyElement);
      this.bodyElement.style.overflow = "hidden";

      await animateTo(
        this.bodyElement,
        shimKeyframesAutoValues(ANIMATION_HIDE.keyframes, {
          height: this.bodyElement.scrollHeight,
        }),
        ANIMATION_HIDE.options,
      );

      this.bodyElement.hidden = true;
      this.bodyElement.style.height = "0px";

      if (this.toggledByMethod) {
        this.stzhClosed.emit({
          component: "stzh-timeline-item",
        });
      }
    }

    this.toggledByMethod = false;
  }

  @Watch("foldable")
  foldableWatcher(newValue: boolean) {
    if (typeof newValue !== "undefined") {
      this._foldable = newValue;
    } else if (this.timeline) {
      this._foldable = this.timeline.foldable;
    }

    if (!this._foldable) {
      this.open = true;
    }
  }

  @Listen("keydown", { target: "document", capture: true })
  handleKeydown(event: KeyboardEvent) {
    if (event.key === "Shift") {
      this.shiftPressed = true;
    }
  }

  @Listen("keyup", { target: "document", capture: true })
  handleKeyup(event: KeyboardEvent) {
    if (event.key === "Shift") {
      this.shiftPressed = false;
    }
  }

  @State() rootTabindex: number = 0;

  @State() _foldable: boolean = false;

  private toggledByMethod: boolean = false;
  private bodyElement: HTMLDivElement;
  private buttonElement: HTMLButtonElement;
  private timelineItemId: string;
  private timeline: HTMLStzhTimelineElement;
  private focusedByInput: boolean = false;
  private shiftPressed: boolean = false;

  private onRootFocus = () => {
    if (!this.focusedByInput) {
      this.buttonElement.focus();
    }

    this.focusedByInput = false;
  };

  private onFocus = (event: FocusEvent) => {
    this.focusedByInput = true;

    if (this.shiftPressed) {
      // user presses currently shift
      this.rootTabindex = -1;
    }

    this.stzhFocus.emit({
      component: "stzh-timeline-item",
      originalEvent: event,
    });
  };

  private onBlur = (event: FocusEvent) => {
    this.rootTabindex = 0;

    this.stzhBlur.emit({
      component: "stzh-timeline-item",
      originalEvent: event,
    });
  };

  private handleFoldableChange = (_event: CustomEvent<StzhTimelineFoldableChangeEvent>) => {
    this.foldableWatcher(this.foldable);
  };

  private handleHeaderClick = () => {
    if (typeof this.collapsible !== "undefined") {
      if (this.collapsible) {
        this.toggle();
      } else if (!this.open) {
        this.show();
      }
    } else if (!this.timeline || this.timeline.collapsible) {
      this.toggle();
    } else if (!this.open) {
      this.show();
    }
  };

  private onButtonKeydown = (event: KeyboardEvent) => {
    let action = null;

    if (event.key === "ArrowDown") {
      action = "next";
    } else if (event.key === "ArrowUp") {
      action = "prev";
    } else if (event.key === "Home") {
      action = "first";
    } else if (event.key === "End") {
      action = "last";
    } else if (event.key === "PageDown" && event.ctrlKey) {
      action = "next";
    } else if (event.key === "PageUp" && event.ctrlKey) {
      action = "prev";
    }

    if (event.key === "Shift") {
      this.rootTabindex = -1;
    }

    if (action) {
      event.preventDefault();
      this.timeline.setItemFocus(this.element, action);
    }
  };

  private onContentKeydown = (event: KeyboardEvent) => {
    if (!event.ctrlKey) {
      return;
    }

    let action = null;

    if (event.key === "PageDown") {
      action = "next";
    } else if (event.key === "PageUp") {
      action = "prev";
    }

    if (action) {
      event.preventDefault();
      this.timeline.setItemFocus(this.element, action);
    }
  };

  componentWillLoad() {
    this.timelineItemId = `stzh-timeline-item-${timelineItemCounter++}`;
  }

  componentDidLoad() {
    this.bodyElement.hidden = !this.open;
    this.bodyElement.style.height = this.open ? "auto" : "0px";
    this.bodyElement.style.overflow = this.open ? "visible" : "hidden";
  }

  async connectedCallback() {
    this.timeline = this.element.closest("stzh-timeline");

    if (this.timeline) {
      this.timeline.addEventListener("stzhFoldableChange", this.handleFoldableChange);
    }

    this.foldableWatcher(this.foldable);
  }

  disconnectedCallback() {
    if (this.timeline) {
      this.timeline.removeEventListener("stzhFoldableChange", this.handleFoldableChange);
      this.timeline = null;
    }
  }

  render() {
    const Heading = `h${this.headingLevel}`;
    const hasIcon = !!this.icon || hasSlot(this.element, "icon");
    const classes = {
      "stzh-timeline-item": true,
      "stzh-timeline-item--has-description": !!this.description,
      "stzh-timeline-item--is-open": this.open,
      "stzh-timeline-item--is-disabled": this.disabled,
      "stzh-timeline-item--is-foldable": this._foldable,
      "stzh-timeline-item--is-completed": this.completed,
    };

    return (
      <Host
        role="listitem"
        is-foldable={this._foldable}
        tabindex={this.disabled ? null : this.rootTabindex}
        onFocus={this.onRootFocus}
      >
        <div class={classes}>
          <Heading
            id={`${this.timelineItemId}-header`}
            class="stzh-timeline-item__heading"
            onClick={this.handleHeaderClick}
          >
            <button
              ref={el => (this.buttonElement = el as HTMLButtonElement)}
              class="stzh-timeline-item__button"
              aria-controls={`${this.timelineItemId}-content`}
              aria-expanded={`${this.open ? "true" : "false"}`}
              aria-disabled={!this._foldable ? "true" : "false"}
              disabled={this.disabled}
              onFocus={this.onFocus}
              onBlur={this.onBlur}
              onKeyDown={this.onButtonKeydown}
              type="button"
              s-object-id={this.analyticsId || this.heading}
            >
              <span class="stzh-timeline-item__bullet">
                {hasIcon || this.completed ? (
                  <span class="stzh-timeline-item__bullet-icon-wrapper">
                    {this.icon || this.completed ? (
                      <stzh-icon
                        class="stzh-timeline-item__bullet-icon"
                        name={this.icon ? this.icon : "checkmark"}
                      ></stzh-icon>
                    ) : (
                      <slot name="icon"></slot>
                    )}
                  </span>
                ) : (
                  <span class="stzh-timeline-item__bullet-text">{this.step}</span>
                )}

                {(this.badge || this.badgeEmpty) && (
                  <stzh-badge class="stzh-timeline-item__badge" label={this.badge} type={this.badgeType}></stzh-badge>
                )}
              </span>
              <div class="stzh-timeline-item__button-inner">
                <span class="stzh-timeline-item__button-text">
                  <span class="stzh-timeline-item__heading-text">{this.heading}</span>
                  <span class="stzh-timeline-item__description">{this.description}</span>
                </span>
                {this._foldable && <span class="stzh-timeline-item__button-icon"></span>}
              </div>
            </button>
          </Heading>
          <div
            id={`${this.timelineItemId}-content`}
            ref={el => (this.bodyElement = el as HTMLDivElement)}
            class="stzh-timeline-item__content"
            aria-labelledby={`${this.timelineItemId}-header`}
            role="region"
            onKeyDown={this.onContentKeydown}
          >
            <div class="stzh-timeline-item__content-inner">
              <slot></slot>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
